<template>
    <div>
        <loading v-if="loading"></loading>
        <draggable
            v-bind="dragOptions"
            v-else
            class="rounded-6 overflow-hidden border border-primary-20"
            :class="customStyling"
            handle=".draggable"
            v-model="list"
            :item-key="item => item.id ?? guidGenerator()"
            @start="drag = true"
            @end="updateOrder"
        >
            <template #item="{ element }">
                <li
                    class="bg-white odd:bg-primary-5 p-12 list-none select-none"
                    @dblclick="edit(element)"
                    @mousedown="currentItem = element"
                >
                    <div class="flex items-center justify-between gap-8">
                        <click-edit-input
                            v-if="clickEdit && editing && editing.id === element.id"
                            class="grow"
                            :data-test="'edit-' + element.id"
                            :value="element[label]"
                            :ref="'edit-' + element.id"
                            @input="editing = null; stopEditing(element, $event)"
                            @blur="editing = null"
                        />
                        <span
                            v-else-if="!!labelByLanguage(element[label])"
                            class="truncate"
                        >
                            {{ labelByLanguage(element[label]) }}
                        </span>
                        <label-with-missing-translation v-else />

                        <div class="flex items-center gap-8">
                            <button class="text-primary-80 hover:text-primary" @click="edit(element)">
                                <svg-icon type="edit"/>
                            </button>
                            <button
                                class="text-primary-80 hover:text-primary"
                                v-if="enableDuplicate"
                                @click="confirmDuplicate(element)"
                            >
                                <svg-icon type="content-copy"/>
                            </button>
                            <button
                                v-if="!disableDelete"
                                class="text-primary-80 hover:text-semantic-error"
                                @click="confirmDelete(element)"
                            >
                                <svg-icon type="delete"/>
                            </button>
                            <button
                                v-if="draggable"
                                class="draggable cursor-grab text-primary-80 hover:text-primary"
                            >
                                <svg-icon type="drag-indicator"/>
                            </button>
                        </div>
                    </div>
                </li>
            </template>
        </draggable>
    </div>
</template>

<script>
import Loading from '../../components/ui/Loading.vue';
import Draggable from "vuedraggable";
import SvgIcon from './SvgIcon'
import {mapState} from "pinia";
import {useMainStore} from '../../state/store';
import LabelWithMissingTranslation from "../LabelWithMissingTranslation.vue";
import ClickEditInput from "../forms/ClickEditInput.vue";
import {guidGenerator} from "../../../shared/helpers";

// VUE3_MIGRATION - temp fix for vuedraggable, compat issue
Draggable.compatConfig = { MODE: 3 }

export default {
    components: {
        ClickEditInput,
        LabelWithMissingTranslation,
        Loading,
        Draggable,
        SvgIcon,
    },
    props: {
        loading: {type: Boolean, default: false},
        options: {type: Array},
        label: {type: String},
        draggable: {type: Boolean, default: true},
        customStyling: {type: String},
        translatable: {type: Boolean, default: false},
        enableDuplicate: Boolean,
        disableDelete: Boolean,
        clickEdit: {
            type: Boolean,
            default: false,
        }
    },
    data() {
        return {
            currentItem: null,
            creating: false,
            editing: null,
            deleting: null,
            drag: false
        }
    },
    computed: {
        ...mapState(useMainStore, ['locale']),
        dragOptions() {
            return {
                animation: 200,
                group: "items",
                disabled: !this.draggable,
                ghostClass: "ghost"
            }
        },
        list: {
            get() {
                return this.options;
            },
            set(newList) {
                this.$emit('updateOptions', newList)
            }
        }
    },
    methods: {
        guidGenerator,
        create() {
            this.creating = true;
        },
        edit (item) {
            if (!this.clickEdit) {
                this.$emit('editing', item);
            } else {
                this.editing = item;
                this.$nextTick(() => {
                    this.$refs['edit-' + item.id].startEdit();
                });
            }
        },
        stopEditing(item, value) {
            this.$emit('update', {item, value});
        },
        confirmDuplicate (item) {
            this.$emit('duplicating', item);
        },
        confirmDelete (item) {
            this.$emit('deleting', item);
        },
        updateOrder(evt) {
            if (!this.draggable) return;

            if (evt.newDraggableIndex === this.currentItem.order) {
                this.currentItem = null;
                this.drag = false;
                return;
            }
            this.$emit('updateOrder', {evt, item: this.currentItem, done: () => {
                this.currentItem = null;
                this.drag = false;
            }})
        },
        labelByLanguage(label) {
            if (this.translatable) {
                return label[this.locale]
            } else {
                return label
            }
        }
    },
}
</script>
